import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, FileText, Trash2 } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { TeamSelect } from '../../components/TeamSelect';
import {
  Button,
  Card,
  ConfirmDialog,
  CopyToast,
  EmptyState,
  Input,
  SearchInput,
  Skeleton,
  Textarea,
  useCopyToast,
} from '../../components/ui';
import {
  useCreatePrompt,
  useCurrentUser,
  useDeletePrompt,
  usePrompts,
  useUpdatePrompt,
} from '../../hooks';
import { PROMPT_BODY_MAX, PROMPT_LABEL_MAX, type Id, type Prompt } from '../../types/api';
import { teamLabel } from '../../lib/team';
import styles from './Prompts.module.css';

const AUTOSAVE_DELAY_MS = 900;
const NEW_PROMPT_LABEL = 'Новый промпт';
const MAX_NAME_ATTEMPTS = 30;

export function PromptsPage() {
  const { can, canChange } = useCurrentUser();
  const roleCanEdit = can('editor');

  const [query, setQuery] = useState('');
  const search = useDebounced(query.trim(), 300);
  const list = usePrompts({ q: search || undefined });

  const [openId, setOpenId] = useState<Id | null>(null);
  const [removing, setRemoving] = useState<Prompt | null>(null);

  const create = useCreatePrompt();
  const remove = useDeletePrompt();
  const toast = useCopyToast();

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const opened = items.find((item) => item.id === openId) ?? null;

  useEffect(() => {
    const first = items[0];
    if (!first) return;
    if (!items.some((item) => item.id === openId)) setOpenId(first.id);
  }, [items, openId]);

  const addPrompt = (attempt = 1) => {
    const label = attempt === 1 ? NEW_PROMPT_LABEL : `${NEW_PROMPT_LABEL} ${attempt}`;
    create.mutate(
      { label, body: '' },
      {
        onSuccess: (prompt) => setOpenId(prompt.id),
        onError: (error) => {
          if (error.status === 409 && attempt < MAX_NAME_ATTEMPTS) addPrompt(attempt + 1);
        },
      },
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          value={query}
          onChange={setQuery}
          placeholder="Поиск по названию"
          ariaLabel="Поиск промптов"
        />
        <span className={styles.total}>
          {list.isLoading ? 'считаем…' : `${list.data?.total ?? 0} промптов`}
        </span>
        {roleCanEdit && (
          <Button variant="primary" onClick={() => addPrompt()} disabled={create.isPending}>
            Добавить промпт
          </Button>
        )}
      </div>

      {list.error && <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />}
      {create.error && <ErrorBanner error={create.error} />}

      {list.isLoading && (
        <Card>
          <div className={styles.loading}>
            <Skeleton width="40%" height={11} />
            <Skeleton width="70%" height={11} />
            <Skeleton width="55%" height={11} />
          </div>
        </Card>
      )}

      {!list.isLoading && items.length === 0 && (
        <Card>
          <EmptyState
            icon={<FileText size={18} strokeWidth={1.5} />}
            title={search ? `По запросу «${search}» ничего` : 'Промптов пока нет'}
            description={search ? 'Попробуйте другое слово из названия.' : undefined}
            action={
              roleCanEdit && !search ? (
                <Button variant="primary" size="sm" onClick={() => addPrompt()}>
                  Добавить промпт
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}

      {items.length > 0 && (
        <div className={styles.split}>
          <nav className={styles.list} aria-label="Список промптов">
            {items.map((prompt) => (
              <button
                key={prompt.id}
                type="button"
                onClick={() => setOpenId(prompt.id)}
                className={prompt.id === openId ? `${styles.row} ${styles.rowOpen}` : styles.row}
              >
                <span className={styles.rowLabel}>{prompt.label}</span>
                <span className={styles.rowBody}>{preview(prompt.body)}</span>
                <span className={styles.rowMeta}>{authorLine(prompt)}</span>
              </button>
            ))}
          </nav>

          {opened && (
            <PromptEditor
              key={opened.id}
              prompt={opened}
              canEdit={roleCanEdit && canChange(opened)}
              roleCanEdit={roleCanEdit}
              onCopy={(text) => toast.copy(text, 'Промпт скопирован')}
              onRemove={() => setRemoving(opened)}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={`Удалить «${removing?.label ?? ''}»?`}
        body="Промпт пропадёт у всей команды."
        confirmLabel="Удалить"
        destructive
        busy={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;
          remove.mutate(removing.id, {
            onSuccess: () => {
              if (openId === removing.id) setOpenId(null);
              setRemoving(null);
            },
          });
        }}
      />

      <CopyToast message={toast.message} />
    </div>
  );
}

function authorLine(prompt: Prompt): string {
  const author = prompt.author_login ?? 'автор не известен';
  return prompt.team ? `${author} · ${teamLabel(prompt.team)}` : author;
}

function PromptEditor({
  prompt,
  canEdit,
  roleCanEdit,
  onCopy,
  onRemove,
}: {
  prompt: Prompt;
  canEdit: boolean;
  roleCanEdit: boolean;
  onCopy: (text: string) => void;
  onRemove: () => void;
}) {
  const update = useUpdatePrompt();
  const [label, setLabel] = useState(prompt.label);
  const [body, setBody] = useState(prompt.body);

  const saved = useRef({ label: prompt.label, body: prompt.body });
  const sent = useRef({ label: prompt.label, body: prompt.body });
  const flush = useRef(() => {});

  flush.current = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (trimmed === sent.current.label && body === sent.current.body) return;
    sent.current = { label: trimmed, body };
    update.mutate(
      { promptId: prompt.id, payload: { label: trimmed, body } },
      { onSuccess: () => (saved.current = { label: trimmed, body }) },
    );
  };

  useEffect(() => {
    const timer = window.setTimeout(() => flush.current(), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [label, body]);

  useEffect(() => () => flush.current(), []);

  const dirty = label.trim() !== saved.current.label || body !== saved.current.body;

  return (
    <Card className={styles.editor}>
      <div className={styles.editorHead}>
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={() => flush.current()}
          maxLength={PROMPT_LABEL_MAX}
          disabled={!canEdit}
          aria-label="Название промпта"
          className={styles.label}
        />
        <TeamSelect
          value={prompt.team}
          disabled={update.isPending}
          onChange={(team) => update.mutate({ promptId: prompt.id, payload: { team } })}
        />
        <Button size="sm" onClick={() => onCopy(body)} disabled={!body}>
          <Copy size={14} strokeWidth={1.5} />
          Копировать
        </Button>
        {canEdit && (
          <Button
            size="sm"
            variant="danger"
            iconOnly
            onClick={onRemove}
            aria-label="Удалить промпт"
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </Button>
        )}
      </div>

      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onBlur={() => flush.current()}
        maxLength={PROMPT_BODY_MAX}
        disabled={!canEdit}
        aria-label="Текст промпта"
        className={styles.body}
        placeholder="Текст, который отправляете нейросети"
      />

      {update.error && <ErrorBanner error={update.error} />}

      <div className={styles.status}>
        {!canEdit && !roleCanEdit && 'роль «наблюдатель»: только просмотр'}
        {!canEdit && roleCanEdit && `автор: ${authorLine(prompt)}. Менять может только автор или администратор`}
        {canEdit && !label.trim() && 'без названия не сохранится'}
        {canEdit && label.trim() !== '' && saveHint(dirty, update.isPending)}
      </div>
    </Card>
  );
}

function saveHint(dirty: boolean, saving: boolean): string {
  if (saving) return 'сохраняем…';
  return dirty ? 'изменения не сохранены' : 'сохранено';
}

function preview(body: string): string {
  const line = body.trim().split('\n', 1)[0] ?? '';
  return line.length > 70 ? `${line.slice(0, 70)}…` : line;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
